import React, { useState } from "react";
import { Check, Plus } from "lucide-react";
import { SC_KEY, SC_COLORS, DEFAULT_LISTS, DEFAULT_XP_ENFANT, DEFAULT_XP_PARENT, DEFAULT_REWARDS, DEFAULT_RAPPELS, XP_PER_LEVEL, ENERGY, DEFAULT_MOMENTS, LAB_QUESTION } from "@/data/superCerveau";

export const IMG = (name) => `/modules/super-cerveau/${name}.jpg`;

export const freshState = () => ({
  setup: { childName: "", parentName: "", started: false },
  lists: { ...DEFAULT_LISTS },
  xpEnfant: DEFAULT_XP_ENFANT,
  xpParent: DEFAULT_XP_PARENT,
  rewards: DEFAULT_REWARDS,
  rappels: DEFAULT_RAPPELS.map((label) => ({ label, gone: false })),
  scale: ENERGY,
  xpDevinette: 1,
  labQuestion: LAB_QUESTION,
  coffre: { message: "", opened: [] },
  depotToken: "",
  days: {},
  weeks: {},
  claims: [],
});

export const loadState = () => {
  try {
    const raw = localStorage.getItem(SC_KEY);
    if (raw) return { ...freshState(), ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return freshState();
};

export const saveState = (s) => { try { localStorage.setItem(SC_KEY, JSON.stringify(s)); } catch { /* ignore */ } };

export const freshDay = () => ({ moments: {}, energie: null, niveau: "", pouvoir: "", obstacle: "", boss: "", bossCause: "", arme: "", xpEnfant: [], xpParent: [], rappelsEvites: 0 });
export const freshWeek = () => ({ secret: "", topPouvoirs: ["", "", ""], topObstacles: ["", "", ""], topBoss: ["", "", ""], lab: {}, labHidden: [], garde: "", laisse: "", teste: "", contrat: {}, victoire: "", badge: "", appris: "" });
export const labQuestionOf = (state) => (state.labQuestion || "").trim() || LAB_QUESTION;

const pad = (n) => String(n).padStart(2, "0");
export const dateKey = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseKey = (k) => { const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d); };
export const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
export const weekKey = (d = new Date()) => dateKey(mondayOf(d));
export const weekDays = (wk) => Array.from({ length: 7 }, (_, i) => { const d = parseKey(wk); d.setDate(d.getDate() + i); return dateKey(d); });
export const fmtDate = (k) => parseKey(k).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
export const fmtShort = (k) => parseKey(k).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" });
export const DAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];

const sumRules = (ids, rules) => ids.reduce((t, id) => t + (rules.find((r) => r.id === id)?.xp || 0), 0);
export const scaleOf = (state) => (state.scale && state.scale.length === 4 ? state.scale : ENERGY);
export const momentsOf = (state) => state.lists.moments || DEFAULT_MOMENTS;
export const momentFinal = (d) => (!d || d.enfant === undefined || d.parent === undefined ? undefined : d.parent === d.enfant ? d.enfant : d.final);
export const dayMatches = (day) => Object.values(day.moments || {}).filter((d) => d.parent !== undefined && d.parent === d.enfant).length;
export const dayXp = (day, state) => ({ enfant: sumRules(day.xpEnfant || [], state.xpEnfant) + dayMatches(day) * (state.xpDevinette ?? 1), parent: sumRules(day.xpParent || [], state.xpParent) });
// Tendance des temps forts sur une liste de jours : moments les plus souvent « compliqués » (niveau <= 1).
export const momentsTrend = (days, moments) => moments.map((m) => {
  const vals = days.map((d) => momentFinal((d.moments || {})[m.id])).filter((v) => v !== undefined);
  const hard = vals.filter((v) => v <= 1).length;
  const matches = days.filter((d) => { const x = (d.moments || {})[m.id]; return x && x.parent !== undefined && x.parent === x.enfant; }).length;
  return { ...m, total: vals.length, hard, matches, avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
}).filter((m) => m.total > 0).sort((a, b) => b.hard - a.hard || a.avg - b.avg);
export const totalXp = (state) => Object.values(state.days).reduce((acc, d) => { const x = dayXp(d, state); return { enfant: acc.enfant + x.enfant, parent: acc.parent + x.parent }; }, { enfant: 0, parent: 0 });
export const spentXp = (state) => state.claims.reduce((t, c) => t + c.xp, 0);
export const levelOf = (xp) => Math.floor(xp / XP_PER_LEVEL) + 1;
export const dayHasData = (d) => !!d && (Object.keys(d.moments || {}).length > 0 || d.energie !== null || d.pouvoir || d.obstacle || d.boss || d.arme || (d.xpEnfant || []).length > 0 || (d.xpParent || []).length > 0);

export const countIn = (days, field) => {
  const c = {};
  days.forEach((d) => { if (d && d[field]) c[d[field]] = (c[d[field]] || 0) + 1; });
  return Object.entries(c).sort((a, b) => b[1] - a[1]);
};

export const Primary = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center justify-center gap-1.5 px-7 py-3 rounded-full bg-[var(--gold)] text-white font-semibold shadow-sm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>{children}</button>
);
export const Ghost = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors ${className}`}>{children}</button>
);

export const Panel = ({ title, sub, color, img, children, testid, className = "" }) => {
  const c = color ? SC_COLORS[color] : null;
  return (
    <section className={`rounded-3xl bg-white/80 border border-[var(--border)] overflow-hidden shadow-sm ${className}`} data-testid={testid}>
      {img && <img src={IMG(img)} alt="" className="w-full h-40 sm:h-52 object-cover sc-hero-fade" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <div className="p-5 sm:p-7">
        {title && (
          <h2 className="font-serif-title text-2xl sm:text-3xl mb-1" style={{ color: c ? `var(--sc-fg-${color}, ${c.fg})` : "var(--gold)" }}>{title}</h2>
        )}
        {sub && <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-4">{sub}</p>}
        {children}
      </div>
    </section>
  );
};

export const Chip = ({ active, color = "pouvoir", onClick, children, testid }) => {
  const c = SC_COLORS[color];
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-full border-2 font-medium transition-all active:scale-95"
      style={active ? { background: c.ring, borderColor: c.ring, color: "#fff" } : { background: c.bg, borderColor: "transparent", color: c.fg }}>
      {active && <Check size={14} />} {children}
    </button>
  );
};

export const Field = ({ label, value, onChange, placeholder, testid, multiline = false }) => (
  <label className="block">
    {label && <span className="block text-sm font-semibold text-[var(--ink)] mb-1.5">{label}</span>}
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} rows={2}
        className="w-full rounded-2xl border border-[var(--border)] bg-white px-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full rounded-full border border-[var(--border)] bg-white px-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
    )}
  </label>
);

export const XpPill = ({ xp, color = "pouvoir" }) => {
  const c = SC_COLORS[color];
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ background: c.bg, color: c.fg }}>+{xp} XP</span>;
};

export const Stars = ({ value = 0, onChange, testid }) => (
  <div className="inline-flex gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n === value ? 0 : n)} aria-label={`${n} étoiles`} data-testid={`${testid}-${n}`}
        className={`text-xl leading-none transition-transform hover:scale-110 ${n <= value ? "text-[#F2B84B]" : "text-[var(--beige)]"}`}>★</button>
    ))}
  </div>
);

export const uid = () => Math.random().toString(36).slice(2, 8);

// Ajout rapide d'une règle XP / récompense : { id, label, xp }.
export const QuickAddRule = ({ onAdd, color = "pouvoir", placeholder, testid, defaultXp = 1 }) => {
  const c = SC_COLORS[color];
  const [label, setLabel] = useState("");
  const [xp, setXp] = useState(defaultXp);
  const submit = () => { const l = label.trim(); if (!l) return; onAdd({ id: uid(), label: l, xp: Math.max(0, Number(xp) || 0) }); setLabel(""); setXp(defaultXp); };
  return (
    <div className="flex gap-2 items-center" data-testid={testid}>
      <input type="number" min={0} max={99} value={xp} onChange={(e) => setXp(e.target.value)} aria-label="XP" data-testid={`${testid}-xp`} className="w-16 rounded-full border-2 bg-white px-2 py-2 text-sm text-center font-bold" style={{ borderColor: c.ring, color: c.fg }} />
      <input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={placeholder} data-testid={`${testid}-input`} className="flex-1 rounded-full border border-[var(--border)] bg-white px-4 py-2 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
      <button onClick={submit} aria-label="Ajouter" data-testid={`${testid}-add`} className="w-10 h-10 rounded-full text-white flex items-center justify-center shrink-0 active:scale-95" style={{ background: c.ring }}><Plus size={18} /></button>
    </div>
  );
};

// Série de missions du soir consécutives (se termine aujourd'hui ou hier).
export const streakOf = (days) => {
  let d = new Date(); let n = 0;
  if (!dayHasData(days[dateKey(d)])) d.setDate(d.getDate() - 1);
  while (dayHasData(days[dateKey(d)])) { n += 1; d.setDate(d.getDate() - 1); }
  return n;
};
export const bestStreakOf = (days) => {
  const keys = Object.keys(days).filter((k) => dayHasData(days[k])).sort();
  let best = 0, cur = 0, prev = null;
  keys.forEach((k) => { const d = parseKey(k); cur = prev && (d - prev) / 86400000 === 1 ? cur + 1 : 1; best = Math.max(best, cur); prev = d; });
  return best;
};
export const STREAK_MILESTONES = [5, 10, 20, 30];
export const COFFRE_EVERY = 5;
export const COFFRE_DEFAULT_MESSAGE = "Je suis tellement fier·e de toi ! Cinq soirs d'affilée, tu as montré à ton cerveau qui est le chef. Continue, super-héros !";
// Coffre surprise : un coffre par palier de 5 soirs consécutifs au sein d'une même série.
export const coffreInfo = (state) => {
  const streak = streakOf(state.days);
  const d = new Date(); if (!dayHasData(state.days[dateKey(d)])) d.setDate(d.getDate() - 1);
  d.setDate(d.getDate() - Math.max(0, streak - 1));
  const runStart = dateKey(d);
  const milestone = Math.floor(streak / COFFRE_EVERY) * COFFRE_EVERY;
  const key = `${runStart}:${milestone}`;
  const opened = (state.coffre?.opened || []).some((o) => o.key === key);
  return { streak, milestone, key, available: milestone >= COFFRE_EVERY && !opened, opened: milestone >= COFFRE_EVERY && opened, remaining: milestone >= COFFRE_EVERY && !opened ? 0 : COFFRE_EVERY - (streak % COFFRE_EVERY) };
};
export const streakBadges = (days) => { const b = bestStreakOf(days); return STREAK_MILESTONES.filter((m) => b >= m).map((m) => `Série de ${m} missions`); };
export const Flame = ({ streak, testid = "sc-streak" }) => (
  <span data-testid={testid} title={`${streak} jour(s) consécutif(s)`} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold ${streak >= 5 ? "bg-[#E0705C] text-white" : streak > 0 ? "bg-[#FDE4DF] text-[#B4483A]" : "bg-[var(--sand)] text-[var(--ink-soft)]"}`}>
    <span className={streak >= 5 ? "animate-pulse" : ""}>🔥</span> {streak} jour{streak > 1 ? "s" : ""}
  </span>
);
